/**
 * localization-form.js  (v8 — pure IP-based, no manual locking)
 *
 * Rules:
 *   India (IN)          → INR
 *   Europe (EU list)    → EUR
 *   Everything else     → USD
 *
 * No manual selection is ever persisted across page loads.
 * Bucks auto-detects currency from IP on every visit.
 * The country picker modal still works for switching markets/catalog
 * (language, taxes, etc.) but does NOT lock currency in cookies/localStorage.
 */

/* ─────────────────────────────────────────────
   Continent lookup table (ISO-2 → region name)
   ───────────────────────────────────────────── */
const CONTINENT_MAP = {
  DZ:'Africa',EG:'Africa',ET:'Africa',GH:'Africa',KE:'Africa',MA:'Africa',
  MZ:'Africa',NG:'Africa',SN:'Africa',ZA:'Africa',TZ:'Africa',TN:'Africa',
  UG:'Africa',CI:'Africa',CM:'Africa',AO:'Africa',BJ:'Africa',BW:'Africa',
  BF:'Africa',BI:'Africa',CV:'Africa',CF:'Africa',TD:'Africa',KM:'Africa',
  CD:'Africa',CG:'Africa',DJ:'Africa',GQ:'Africa',ER:'Africa',SZ:'Africa',
  GA:'Africa',GM:'Africa',GN:'Africa',GW:'Africa',LS:'Africa',LR:'Africa',
  LY:'Africa',MG:'Africa',MW:'Africa',ML:'Africa',MR:'Africa',MU:'Africa',
  NA:'Africa',NE:'Africa',RW:'Africa',ST:'Africa',SC:'Africa',SL:'Africa',
  SO:'Africa',SS:'Africa',SD:'Africa',TG:'Africa',ZM:'Africa',ZW:'Africa',
  RE:'Africa',YT:'Africa',
  AR:'Americas',BO:'Americas',BR:'Americas',CA:'Americas',CL:'Americas',
  CO:'Americas',CR:'Americas',CU:'Americas',DO:'Americas',EC:'Americas',
  SV:'Americas',GT:'Americas',HT:'Americas',HN:'Americas',JM:'Americas',
  MX:'Americas',NI:'Americas',PA:'Americas',PY:'Americas',PE:'Americas',
  PR:'Americas',TT:'Americas',US:'Americas',UY:'Americas',VE:'Americas',
  BB:'Americas',BZ:'Americas',GY:'Americas',SR:'Americas',
  AF:'Asia',AM:'Asia',AZ:'Asia',BH:'Asia',BD:'Asia',BT:'Asia',BN:'Asia',
  KH:'Asia',CN:'Asia',CY:'Asia',GE:'Asia',HK:'Asia',IN:'Asia',ID:'Asia',
  IR:'Asia',IQ:'Asia',IL:'Asia',JP:'Asia',JO:'Asia',KZ:'Asia',KW:'Asia',
  KG:'Asia',LA:'Asia',LB:'Asia',MO:'Asia',MY:'Asia',MV:'Asia',MN:'Asia',
  MM:'Asia',NP:'Asia',KP:'Asia',OM:'Asia',PK:'Asia',PS:'Asia',PH:'Asia',
  QA:'Asia',SA:'Asia',SG:'Asia',KR:'Asia',LK:'Asia',SY:'Asia',TW:'Asia',
  TJ:'Asia',TH:'Asia',TL:'Asia',TR:'Asia',TM:'Asia',AE:'Asia',UZ:'Asia',
  VN:'Asia',YE:'Asia',
  AL:'Europe',AD:'Europe',AT:'Europe',BY:'Europe',BE:'Europe',BA:'Europe',
  BG:'Europe',HR:'Europe',CZ:'Europe',DK:'Europe',EE:'Europe',FI:'Europe',
  FR:'Europe',DE:'Europe',GR:'Europe',HU:'Europe',IS:'Europe',IE:'Europe',
  IT:'Europe',XK:'Europe',LV:'Europe',LI:'Europe',LT:'Europe',LU:'Europe',
  MT:'Europe',MD:'Europe',MC:'Europe',ME:'Europe',NL:'Europe',MK:'Europe',
  NO:'Europe',PL:'Europe',PT:'Europe',RO:'Europe',RU:'Europe',SM:'Europe',
  RS:'Europe',SK:'Europe',SI:'Europe',ES:'Europe',SE:'Europe',CH:'Europe',
  UA:'Europe',GB:'Europe',VA:'Europe',AX:'Europe',GI:'Europe',
  AU:'Oceania',FJ:'Oceania',NZ:'Oceania',PG:'Oceania',WS:'Oceania',
  SB:'Oceania',TO:'Oceania',VU:'Oceania',CK:'Oceania',PF:'Oceania',
  GU:'Oceania',NC:'Oceania',
};

const REGION_ORDER = ['Africa','Americas','Asia','Europe','Oceania','Other'];

function getRegion(iso) { return CONTINENT_MAP[iso] || 'Other'; }

/* ─────────────────────────────────────────────
   Currency map — country ISO → currency
   India → INR, Europe → EUR, everything else → USD
   ───────────────────────────────────────────── */
var CURRENCY_MAP = {
  AD:'EUR',AL:'EUR',AT:'EUR',AX:'EUR',BA:'EUR',BE:'EUR',BG:'EUR',BL:'EUR',
  BY:'EUR',CH:'EUR',CY:'EUR',CZ:'EUR',DE:'EUR',DK:'EUR',EE:'EUR',ES:'EUR',
  FI:'EUR',FR:'EUR',GB:'EUR',GE:'EUR',GF:'EUR',GI:'EUR',GP:'EUR',GR:'EUR',
  HR:'EUR',HU:'EUR',IE:'EUR',IS:'EUR',IT:'EUR',LI:'EUR',LT:'EUR',LU:'EUR',
  LV:'EUR',MC:'EUR',MD:'EUR',ME:'EUR',MF:'EUR',MK:'EUR',MQ:'EUR',MT:'EUR',
  NL:'EUR',NO:'EUR',PL:'EUR',PM:'EUR',PT:'EUR',RE:'EUR',RO:'EUR',RS:'EUR',
  RU:'EUR',SE:'EUR',SI:'EUR',SK:'EUR',SM:'EUR',TR:'EUR',UA:'EUR',VA:'EUR',
  XK:'EUR',YT:'EUR',
  IN:'INR'
};

function getCurrencyForCountry(isoCode) {
  return CURRENCY_MAP[isoCode] || 'USD';
}

/* ─────────────────────────────────────────────
   STEP 1 — Clear any stale manually-locked currency cookies/storage
   on every page load so Bucks can always IP-detect freely.
   ───────────────────────────────────────────── */
(function clearStaleCurrencyLocks() {
  var expPast = new Date(0);
  /* Expire all currency cookies so Bucks reads from IP, not stale state */
  ['bucksCurrency','bucks_currency','currency','cart_currency'].forEach(function(k) {
    document.cookie = k + '=; path=/; expires=' + expPast.toUTCString();
  });
  /* Clear all localStorage currency keys */
  try {
    ['bucksCurrency','bucks_currency','currency','nova_currency',
     'mlveda_currency','user_chosen_currency','user_country_manual',
     'country_auto_set'].forEach(function(k) {
      localStorage.removeItem(k);
    });
  } catch(e) {}
})();


/* ─────────────────────────────────────────────
   Bucks currency sync
   Called after country switch to apply the correct currency.
   ───────────────────────────────────────────── */
function setBucksCurrency(currencyCode) {
  if (!currencyCode) return;

  /* Call Bucks API directly — this is the primary mechanism */
  ['Bucks','BucksApp','bucks','BucksCC','buckscc'].forEach(function(name) {
    try {
      var obj = window[name];
      if (obj && typeof obj.setCurrency === 'function') obj.setCurrency(currencyCode);
    } catch(e) {}
  });

  /* Click matching Bucks selector option */
  var sel = document.getElementById('bucksSelector');
  if (sel && sel.value !== currencyCode) {
    sel.value = currencyCode;
    sel.dispatchEvent(new Event('change', { bubbles: true }));
  }
  document.querySelectorAll('.buckscc-select-options li').forEach(function(li) {
    if (li.getAttribute('rel') === currencyCode || li.id === currencyCode) {
      li.click();
    }
  });

  /* Update Shopify currency object */
  try {
    window.Shopify = window.Shopify || {};
    window.Shopify.currency = window.Shopify.currency || {};
    window.Shopify.currency.active = currencyCode;
  } catch(e) {}

  /* Fire currency change events */
  ['bucks:currency:change','currency:change','bucks:set:currency'].forEach(function(evName) {
    try {
      var ev = new CustomEvent(evName, { detail: { currency: currencyCode }, bubbles: true });
      document.dispatchEvent(ev);
      window.dispatchEvent(ev);
    } catch(e) {}
  });
}


/* ─────────────────────────────────────────────
   Modal controller (singleton)
   ───────────────────────────────────────────── */
var _modalBuilt = false;

function buildModal() {
  if (_modalBuilt) return;
  _modalBuilt = true;

  var overlay = document.getElementById('country-modal-overlay');
  if (!overlay) return;

  if (typeof window.__applyRealCurrencies === 'function') {
    window.__applyRealCurrencies();
  }

  /* Group countries by continent */
  var rawItems = Array.from(overlay.querySelectorAll('#country-modal-list .country-modal__item'));
  var groups   = {};
  rawItems.forEach(function(btn) {
    var region = getRegion(btn.dataset.countryIso);
    if (!groups[region]) groups[region] = [];
    groups[region].push(btn);
  });

  var listEl = overlay.querySelector('#country-modal-list');
  listEl.innerHTML = '';

  REGION_ORDER.forEach(function(region) {
    if (!groups[region] || !groups[region].length) return;
    var section = document.createElement('div');
    section.className      = 'country-modal__region';
    section.dataset.region = region;

    var heading = document.createElement('h3');
    heading.className   = 'country-modal__region-title';
    heading.textContent = region;
    section.appendChild(heading);

    var grid = document.createElement('div');
    grid.className = 'country-modal__grid';
    groups[region].forEach(function(btn) { grid.appendChild(btn); });
    section.appendChild(grid);
    listEl.appendChild(section);
  });

  /* Search */
  var searchInput = overlay.querySelector('.country-modal__search');
  var noResults   = overlay.querySelector('.country-modal__no-results');

  searchInput.addEventListener('input', function() {
    var q = searchInput.value.trim().toLowerCase();
    var total = 0;
    overlay.querySelectorAll('.country-modal__item').forEach(function(btn) {
      var nameMatch     = (btn.dataset.countryName     || '').toLowerCase().includes(q);
      var currencyMatch = (btn.dataset.countryCurrency || '').toLowerCase().includes(q);
      var match = !q || nameMatch || currencyMatch;
      btn.style.display = match ? '' : 'none';
      if (match) total++;
    });
    overlay.querySelectorAll('.country-modal__region').forEach(function(sec) {
      var anyVisible = Array.from(sec.querySelectorAll('.country-modal__item')).some(function(b) {
        return b.style.display !== 'none';
      });
      sec.style.display = anyVisible ? '' : 'none';
    });
    noResults.classList.toggle('visible', total === 0);
  });

  /* Country click → submit localization */
  overlay.addEventListener('click', function(e) {
    var btn = e.target.closest('.country-modal__item');
    if (!btn) return;
    submitLocalization(btn.dataset.countryIso, btn.dataset.countryCurrency);
  });

  overlay.querySelector('.country-modal__close').addEventListener('click', closeModal);
  overlay.addEventListener('click', function(e) { if (e.target === overlay) closeModal(); });
  document.addEventListener('keydown', function(e) { if (e.key === 'Escape') closeModal(); });
}

function openModal() {
  buildModal();
  var overlay = document.getElementById('country-modal-overlay');
  if (!overlay) return;
  if (typeof window.__applyRealCurrencies === 'function') window.__applyRealCurrencies();
  overlay.classList.add('is-open');
  document.body.classList.add('modal-open');
  var search = overlay.querySelector('.country-modal__search');
  if (search) { search.value = ''; search.dispatchEvent(new Event('input')); search.focus(); }
}

function closeModal() {
  var overlay = document.getElementById('country-modal-overlay');
  if (overlay) overlay.classList.remove('is-open');
  document.body.classList.remove('modal-open');
}

function submitLocalization(isoCode, currencyCode) {
  closeModal();

  /* Resolve currency purely from country code — no cookies, no storage */
  var resolvedCurrency = getCurrencyForCountry(isoCode);

  /* Apply currency to Bucks for this session only — nothing is saved to storage */
  setBucksCurrency(resolvedCurrency);

  /* POST to Shopify localization to switch market/catalog (language, taxes, pricing) */
  var forms = Array.from(document.querySelectorAll('form.localization-form'));
  var form  = forms.find(function(f) { return f.querySelector('input[name="country_code"]'); }) || forms[0];

  if (form) {
    var hiddenInput = form.querySelector('input[name="country_code"]');
    if (hiddenInput) hiddenInput.value = isoCode;
    try {
      var returnPath = window.location.pathname + '?currency=' + encodeURIComponent(resolvedCurrency);
      var actionUrl  = new URL(form.action || '/localization', window.location.origin);
      actionUrl.searchParams.set('return_to', returnPath);
      form.action = actionUrl.toString();
    } catch(e) {}
    form.submit();
  } else {
    window.location.href = window.location.pathname + '?currency=' + encodeURIComponent(resolvedCurrency);
  }
}


/* ─────────────────────────────────────────────
   Hide Bucks floating/embed UI — keep conversion running
   ───────────────────────────────────────────── */
function hideBucksUI() {
  document.querySelectorAll('.buckscc-floating-box, .buckscc-currency-box').forEach(function(el) {
    if (!el.closest('#footer-bucks-embed')) {
      el.style.cssText = 'display:none!important';
    }
  });
  var embed = document.getElementById('footer-bucks-embed');
  if (embed) {
    embed.style.cssText = 'position:absolute!important;width:1px!important;height:1px!important;overflow:hidden!important;opacity:0!important;pointer-events:none!important;clip:rect(0,0,0,0)!important';
  }
}

/* ─────────────────────────────────────────────
   Fix EUR comma decimal → dot decimal
   ───────────────────────────────────────────── */
function fixPriceFormat() {
  document.querySelectorAll('.buckscc-money, .money').forEach(function(el) {
    var text = el.textContent || '';
    if (/[€£¥₹]/.test(text) || text.match(/^\s*[A-Z]{0,3}\s*[\d.,]+\s*[A-Z]{0,3}\s*$/)) {
      if (text.match(/,\d{2}(\s*[A-Z]*\s*)$/)) {
        var fixed = text
          .replace(/(\d)\.(\d{3})/g, '$1$2')
          .replace(/,(\d{2})(\s*[A-Z]*\s*)$/, '.$1$2');
        if (fixed !== text) el.textContent = fixed;
      }
    }
  });
}

function observePriceChanges() {
  fixPriceFormat();
  var observer = new MutationObserver(function() { fixPriceFormat(); });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

/* ─────────────────────────────────────────────
   Boot
   ───────────────────────────────────────────── */
function boot() {
  hideBucksUI();
  setTimeout(hideBucksUI, 1000);
  setTimeout(hideBucksUI, 3000);

  document.querySelectorAll('.country-modal-trigger').forEach(function(btn) {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      openModal();
    });
  });

  [500, 1000, 2000, 3000].forEach(function(ms) { setTimeout(fixPriceFormat, ms); });
  window.addEventListener('load', observePriceChanges);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

function wireTrigger() {
  document.querySelectorAll('.country-modal-trigger').forEach(function(btn) {
    btn.onclick = function(e) { e.preventDefault(); openModal(); };
  });
}
wireTrigger();
document.addEventListener('DOMContentLoaded', wireTrigger);
window.addEventListener('load', wireTrigger);